import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Checkbox,
    Dialog,
    DialogContent,
    FormControlLabel,
    IconButton,
    Link,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";

import { useTrackEvent } from "../hooks/useTrackEvent";
import { clickableCard, colors, radius, shadows } from "../theme/tokens";
import { PdfPages } from "./PdfPages";

import type { WidgetOfType } from "../schema/widgets";
import type { WidgetProps } from "./types";
import type { ReactNode } from "react";

// The Programs page rendered as the B2C diagram on the Notion Programs &
// Playbooks page (Bill, 2026-09-05): four tier rows, one white tile per
// program with a checkbox top-left, the title, and a two-tone icon
// bottom-right. Since 2026-09-06 (Bill) the rows run Foundation first down to
// Strategic (the eye lands top-left), each row is a title over a light gray
// rule with no box around it, every tile is one share of a six-column grid (a
// tier longer than six, Growth since 2026-10-25, wraps to a second row), and
// tiles wrap two-up on phones.
//
// The checkbox enables the program for this partnership. The choice persists
// server-side in the room state sidecar (/api/rooms/{slug}/state,
// programStatus) so every visitor to the room sees the same set, and an
// enabled tile reads light green. Clicking the tile itself opens a modal with
// the program write-up, the same enable toggle, and any documents (PDF
// brochures embedded inline). No side drawer, no view toggle, no pyramid.

type Program = WidgetOfType<"programs_explorer">["props"]["programs"][number];
type Tier = Program["tier"];

const TIER_ORDER: Tier[] = ["foundation", "growth", "scale", "strategic"];
const TIER_LABEL: Record<Tier, string> = {
    strategic: "IV. Strategic",
    scale: "III. Scale",
    growth: "II. Growth",
    foundation: "I. Foundation",
};

// Enabled tiles read light green (Bill, 2026-09-05). This is the one green in
// the deal room: it marks a supplier choice, not a verdict.
const ENABLED = { bg: "#EAF6EC", border: "#9CCFA8", check: "#2E7D32" } as const;
const CHECKBOX_IDLE = "#B5B5BA";

// Six tiles to a row on desktop (Foundation's width); a longer tier wraps in
// catalogue order (Bill, 2026-10-25).
const COLUMNS = 6;

interface RoomState {
    programStatus?: Record<string, boolean>;
}

// Rooms stored before canonical ids landed carry programs without `id`;
// fall back to the title so the checkbox still has a stable key.
const programKey = (p: Program) => p.id ?? p.title;

export function ProgramsExplorer({ widget, room }: WidgetProps<"programs_explorer">) {
    const slug = room.slug;
    const trackEvent = useTrackEvent(slug);
    const programs = useMemo(
        () => widget.props.programs.filter((p) => !p.hidden),
        [widget.props.programs],
    );
    const [enabled, setEnabled] = useState<Record<string, boolean>>({});
    const [openKey, setOpenKey] = useState<string | null>(null);

    // Server state first, so every visit shows the latest checkboxes.
    useEffect(() => {
        let cancelled = false;
        fetch(`/api/rooms/${slug}/state`, { cache: "no-store" })
            .then((r) => r.json() as Promise<RoomState>)
            .then((s) => {
                if (!cancelled) setEnabled(s.programStatus ?? {});
            })
            .catch(() => {
                // Render unchecked; the next toggle re-syncs.
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    const toggle = useCallback(
        (program: Program) => {
            const key = programKey(program);
            const next = !enabled[key];
            setEnabled((prev) => ({ ...prev, [key]: next }));
            fetch(`/api/rooms/${slug}/state`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ programStatus: { [key]: next } }),
            }).catch(() => {
                setEnabled((prev) => ({ ...prev, [key]: !next }));
            });
            trackEvent("program_toggled", { programId: key, enabled: next, widgetId: widget.id });
        },
        [enabled, slug, trackEvent, widget.id],
    );

    const open = useCallback(
        (program: Program) => {
            const key = programKey(program);
            setOpenKey(key);
            trackEvent("program_opened", { programId: key, widgetId: widget.id });
        },
        [trackEvent, widget.id],
    );

    const openProgram = openKey
        ? (programs.find((p) => programKey(p) === openKey) ?? null)
        : null;

    // Every tile is one share of a six-column grid, so shorter rows (three
    // Strategic tiles) stay left-aligned at the same tile size, the way the
    // Notion diagram reads.
    return (
        <Stack spacing={2.5}>
            {TIER_ORDER.map((tier) => {
                const items = programs.filter((p) => p.tier === tier);
                if (items.length === 0) return null;
                return (
                    <Box key={tier}>
                        <Typography
                            component="h2"
                            sx={{ fontWeight: 700, color: colors.navy, fontSize: "1.05rem", mb: 1 }}
                        >
                            {TIER_LABEL[tier]}
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                                pt: 1.5,
                                borderTop: 1,
                                borderColor: colors.line,
                            }}
                        >
                            {items.map((p) => {
                                const key = programKey(p);
                                return (
                                    <ProgramTile
                                        key={key}
                                        program={p}
                                        columns={COLUMNS}
                                        enabled={enabled[key] === true}
                                        onOpen={() => open(p)}
                                        onToggle={() => toggle(p)}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                );
            })}

            <ProgramDialog
                program={openProgram}
                enabled={openProgram ? enabled[programKey(openProgram)] === true : false}
                onToggle={() => {
                    if (openProgram) toggle(openProgram);
                }}
                onClose={() => setOpenKey(null)}
            />
        </Stack>
    );
}

// ─── Tile ────────────────────────────────────────────────────────────

interface ProgramTileProps {
    program: Program;
    columns: number;
    enabled: boolean;
    onOpen: () => void;
    onToggle: () => void;
}

function ProgramTile({ program, columns, enabled, onOpen, onToggle }: ProgramTileProps) {
    return (
        <Box
            role="button"
            tabIndex={0}
            aria-label={`${program.title}: read more`}
            onClick={onOpen}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen();
                }
            }}
            sx={{
                ...clickableCard,
                position: "relative",
                // 12px gaps (gap: 1.5) between `columns` tiles.
                flex: {
                    xs: "0 0 calc(50% - 6px)",
                    sm: `0 0 calc((100% - ${(columns - 1) * 12}px) / ${columns})`,
                },
                minWidth: 0,
                minHeight: 92,
                display: "flex",
                flexDirection: "column",
                // Title top sits level with the top of the checkbox square
                // (Bill, 2026-09-06): the checkbox svg starts 11px down (2px
                // offset + 9px button padding) and its square about 3px inside
                // that; 12px of padding puts the first line's cap height there.
                justifyContent: "flex-start",
                pl: 5,
                pr: 1.5,
                pt: 1.5,
                pb: 3.5,
                borderRadius: `${radius.md}px`,
                cursor: "pointer",
                outline: "none",
                ...(enabled
                    ? {
                          bgcolor: ENABLED.bg,
                          borderColor: ENABLED.border,
                          "&:hover": { borderColor: ENABLED.check, boxShadow: shadows.md },
                      }
                    : {}),
                "&:focus-visible": { outline: `2px solid ${colors.navy}`, outlineOffset: 2 },
            }}
        >
            <Checkbox
                checked={enabled}
                size="small"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={onToggle}
                slotProps={{ input: { "aria-label": `Enable ${program.title}` } }}
                sx={{
                    position: "absolute",
                    top: 2,
                    left: 2,
                    color: CHECKBOX_IDLE,
                    "&.Mui-checked": { color: ENABLED.check },
                    "& .MuiSvgIcon-root": { fontSize: 22 },
                }}
            />
            <Typography
                sx={{ fontWeight: 700, color: colors.navy, fontSize: "0.92rem", lineHeight: 1.25 }}
            >
                {program.title}
            </Typography>
            {program.icon && (
                <Box
                    component="img"
                    src={`/program-icons/${program.icon}`}
                    alt=""
                    aria-hidden
                    sx={{ position: "absolute", right: 10, bottom: 8, width: 26, height: 26 }}
                />
            )}
        </Box>
    );
}

// ─── Modal ───────────────────────────────────────────────────────────

interface ProgramDialogProps {
    program: Program | null;
    enabled: boolean;
    onToggle: () => void;
    onClose: () => void;
}

function ProgramDialog({ program, enabled, onToggle, onClose }: ProgramDialogProps) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    return (
        <Dialog
            open={program !== null}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={fullScreen}
            scroll="paper"
            slotProps={{ paper: { sx: { borderRadius: fullScreen ? 0 : `${radius.lg}px` } } }}
        >
            {program && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: { xs: "wrap", sm: "nowrap" },
                            alignItems: "flex-start",
                            gap: 2,
                            px: { xs: 2.5, md: 4 },
                            pt: { xs: 2.5, md: 3.5 },
                            pb: 2,
                            borderBottom: 1,
                            borderColor: "divider",
                        }}
                    >
                        {program.icon && (
                            <Box
                                component="img"
                                src={`/program-icons/${program.icon}`}
                                alt=""
                                aria-hidden
                                sx={{ width: 40, height: 40, flexShrink: 0, mt: 0.5 }}
                            />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="overline" sx={{ display: "block", lineHeight: 1.4 }}>
                                {TIER_LABEL[program.tier]}
                            </Typography>
                            <Typography variant="h4" component="h2">
                                {program.title}
                            </Typography>
                        </Box>
                        {/* The enable toggle sits on the title row, right of the H1 and
                            level with it, not under it (Bill, 2026-09-06). On a phone it
                            wraps to its own row under the title (order last, full basis),
                            so the title keeps the line and the close button stays top-right;
                            on the same row it pushed the X off screen and sat on the title
                            (Bill, 2026-09-09). */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={enabled}
                                    onChange={onToggle}
                                    sx={{
                                        color: CHECKBOX_IDLE,
                                        "&.Mui-checked": { color: ENABLED.check },
                                    }}
                                />
                            }
                            label={enabled ? "Enabled for this partnership" : "Enable this program"}
                            sx={{
                                alignSelf: "center",
                                order: { xs: 1, sm: 0 },
                                flexBasis: { xs: "100%", sm: "auto" },
                                // Phone: the checkbox lines up with the title (icon + gap, less
                                // the label control's own 11px outdent). Desktop: 40px clear of
                                // the close button (Bill, 2026-09-06, three nudges).
                                ml: { xs: program.icon ? "45px" : "-11px", sm: "-11px" },
                                mr: { xs: 0, sm: 5 },
                                // 9px under the title on a phone: the 16px row gap less 7 (12px, then
                                // 25% less; Bill, 2026-09-09).
                                mt: { xs: -0.875, sm: 0 },
                                whiteSpace: "nowrap",
                                "& .MuiFormControlLabel-label": {
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                    color: enabled ? ENABLED.check : colors.navy,
                                },
                            }}
                        />
                        <IconButton onClick={onClose} aria-label="Close" size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <DialogContent sx={{ px: { xs: 2.5, md: 4 }, py: 3 }}>
                        {program.summary && (
                            <Typography variant="body1" sx={{ mb: 2.5, color: "text.secondary" }}>
                                {program.summary}
                            </Typography>
                        )}
                        <Box
                            sx={{
                                "& > *:first-of-type": { mt: 0 },
                                "& > *:last-child": { mb: 0 },
                            }}
                        >
                            <ReactMarkdown components={markdownComponents}>{program.body}</ReactMarkdown>
                        </Box>
                        {program.documents && program.documents.length > 0 && (
                            <Box sx={{ mt: 4 }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, color: "primary.main", mb: 1.5 }}
                                >
                                    Documents
                                </Typography>
                                <Stack spacing={3}>
                                    {program.documents.map((doc) => (
                                        <Box key={doc.url}>
                                            {/* Download sits in the viewer bar, left of full
                                                screen (Bill, 2026-10-25); the pill under the
                                                viewer is gone. */}
                                            <PdfPages url={doc.url} title={doc.title} />
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </DialogContent>
                </>
            )}
        </Dialog>
    );
}

// ─── Markdown ────────────────────────────────────────────────────────

// Shared with the checklist's step guide popup (MAP.tsx), which reads the same.
export const markdownComponents = {
    h2: ({ children }: { children?: ReactNode }) => (
        <Typography
            variant="subtitle1"
            sx={{ mt: 2.5, mb: 1, fontWeight: 600, color: "primary.main" }}
        >
            {children}
        </Typography>
    ),
    h3: ({ children }: { children?: ReactNode }) => (
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.75, fontWeight: 600 }}>
            {children}
        </Typography>
    ),
    p: ({ children }: { children?: ReactNode }) => (
        <Typography variant="body2" sx={{ mb: 1.5 }}>
            {children}
        </Typography>
    ),
    ul: ({ children }: { children?: ReactNode }) => (
        <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
            {children}
        </Box>
    ),
    ol: ({ children }: { children?: ReactNode }) => (
        <Box component="ol" sx={{ pl: 3, mb: 1.5 }}>
            {children}
        </Box>
    ),
    li: ({ children }: { children?: ReactNode }) => (
        <Typography component="li" variant="body2" sx={{ mb: 0.4 }}>
            {children}
        </Typography>
    ),
    strong: ({ children }: { children?: ReactNode }) => (
        <Box component="strong" sx={{ fontWeight: 600, color: "primary.main" }}>
            {children}
        </Box>
    ),
    a: ({ children, href }: { children?: ReactNode; href?: string }) => (
        <Link href={href} target="_blank" rel="noopener noreferrer">
            {children}
        </Link>
    ),
};
